// PHASE-2.03 START
'use client';
import React from 'react';
import { useChat } from '@/context/ChatContext';
import { useClient } from '@/context/ClientContext';
import { HistoryToolbar } from './HistoryToolbar';
import { HistoryList } from './HistoryList';
import { Button } from '@/components/ui/Button';
import type { HistoryItem, HistoryGroup } from './types';

function toDate(value: any): Date | null {
  if (!value) return null;
  // Firestore Timestamp compatibility
  // @ts-ignore
  if (typeof value?.toDate === 'function') return value.toDate();
  try { return new Date(value); } catch { return null; }
}

function groupByDate(items: HistoryItem[]): HistoryGroup[] {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  // Use rolling 7 days window instead of calendar week to match expectations
  const startOfLast7Days = new Date(startOfToday);
  startOfLast7Days.setDate(startOfLast7Days.getDate() - 7);

  const groups: Record<string, HistoryItem[]> = { Today: [], Yesterday: [], 'This week': [], Older: [] };
  for (const it of items) {
    const d = new Date(it.ts);
    if (d >= startOfToday) groups['Today'].push(it);
    else if (d >= startOfYesterday) groups['Yesterday'].push(it);
    else if (d >= startOfLast7Days) groups['This week'].push(it);
    else groups['Older'].push(it);
  }
  return [
    { label: 'Today', items: groups['Today'] },
    { label: 'Yesterday', items: groups['Yesterday'] },
    { label: 'This week', items: groups['This week'] },
    { label: 'Older', items: groups['Older'] },
  ];
}

export default function ConversationHistoryPanel() {
  const { conversations, currentConversation, loadConversation, deleteConversation, createNewConversation } = useChat() as any;
  const { clients } = useClient();

  const [query, setQuery] = React.useState('');
  const [client, setClient] = React.useState<'all' | string>('all');
  const [pinnedOnly, setPinnedOnly] = React.useState(false);

  const [pinnedIds, setPinnedIds] = React.useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('chat.pinnedConversations') || '[]'); } catch { return []; }
  });
  const togglePin = React.useCallback((id: string) => {
    setPinnedIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try { localStorage.setItem('chat.pinnedConversations', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);
  const handleDelete = React.useCallback(async (id: string) => {
    try {
      if (!confirm('Delete this conversation permanently?')) return;
      await deleteConversation(id);
      setPinnedIds(prev => prev.filter(x => x !== id));
    } catch (e) {
      console.error('Failed to delete conversation', e);
    }
  }, [deleteConversation]);

  const handleNewChat = React.useCallback(async () => {
    try {
      if (createNewConversation) {
        const id = await createNewConversation();
        if (id && loadConversation) await loadConversation(id);
      }
    } catch (e) {
      console.error('Failed to create new chat', e);
    }
  }, [createNewConversation, loadConversation]);

  const clientOptions = React.useMemo(() => clients.map(c => ({ id: c.id, name: c.name })), [clients]);

  const items: HistoryItem[] = React.useMemo(() => {
    return conversations.map((c: any) => {
      const tsDate = (toDate(c.updatedAt) || toDate(c.createdAt));
      const ts = tsDate ? tsDate : new Date(0);
      const clientName = clientOptions.find(co => co.id === c.clientId)?.name;
      return {
        id: c.id,
        title: c.title || 'Untitled Conversation',
        excerpt: c.lastMessage || 'No messages yet',
        ts: ts.toISOString(),
        pinned: pinnedIds.includes(c.id),
        unread: false,
        client: clientName,
      } as HistoryItem;
    });
  }, [conversations, pinnedIds, clientOptions]);

  const filtered = items.filter(i => {
    if (pinnedOnly && !i.pinned) return false;
    if (client !== 'all' && i.client !== client) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!(i.title.toLowerCase().includes(q) || i.excerpt.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  // Separate pinned and unpinned
  // Order pinned by most recently updated (desc)
  const pinned = React.useMemo(
    () => filtered.filter(i => i.pinned).sort((a,b) => new Date(b.ts).getTime() - new Date(a.ts).getTime()),
    [filtered]
  );
  const unpinned = filtered.filter(i => !i.pinned);

  // Simple client-side pagination over unpinned items
  const [pageSize, setPageSize] = React.useState(30);
  const flatUnpinned = unpinned;
  const visibleUnpinned = React.useMemo(() => flatUnpinned.slice(0, pageSize), [flatUnpinned, pageSize]);
  const dateGroups = groupByDate(visibleUnpinned);
  const hasMore = flatUnpinned.length > pageSize;
  const [selectedId, setSelectedId] = React.useState<string | undefined>(currentConversation?.id);

  React.useEffect(() => { setSelectedId(currentConversation?.id); }, [currentConversation?.id]);

  const flatIds = filtered.map(i => i.id);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (flatIds.length === 0) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = selectedId ? flatIds.indexOf(selectedId) : -1;
        const nextIdx = e.key === 'ArrowDown' ? Math.min(idx + 1, flatIds.length - 1) : Math.max(idx - 1, 0);
        setSelectedId(flatIds[nextIdx]);
      }
      if (e.key === 'Enter' && selectedId) {
        loadConversation(selectedId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, flatIds, loadConversation]);

  return (
    <div className="h-full w-full flex flex-col min-h-0 p-3 liquid-glass">
      {/* New Chat button */}
      <div className="mb-3">
        <Button
          onClick={handleNewChat}
          variant="glow"
          size="md"
          className="w-full rounded-xl shadow-lg shadow-primary/20"
        >
          New Chat
        </Button>
      </div>
      <div className="mb-3 sticky top-0 z-10 bg-background/60 backdrop-blur-xl border-b border-border/30 pb-3">
        <HistoryToolbar
          query={query}
          setQuery={setQuery}
          client={client}
          setClient={setClient}
          pinnedOnly={pinnedOnly}
          setPinnedOnly={setPinnedOnly}
          clients={clientOptions.map(c => c.name)}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {/* Pinned group first */}
        {pinned.length > 0 && (
          <section aria-labelledby={`h-pinned`} className="space-y-1 mb-4">
            <h2 id={`h-pinned`} className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider px-1 mb-1">Pinned</h2>
            <HistoryList
              items={pinned}
              onSelect={(id) => { setSelectedId(id); loadConversation(id); }}
              selectedId={selectedId}
              pinnedIds={pinnedIds}
              onPinToggle={togglePin}
              onDelete={handleDelete}
            />
          </section>
        )}
        {dateGroups.map(group => group.items.length > 0 && (
          <section key={group.label} aria-labelledby={`h-${group.label}`} className="space-y-1 mb-4">
            <h2 id={`h-${group.label}`} className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider px-1 mb-1">{group.label}</h2>
            <HistoryList
              items={group.items}
              onSelect={(id) => { setSelectedId(id); loadConversation(id); }}
              selectedId={selectedId}
              pinnedIds={pinnedIds}
              onPinToggle={togglePin}
              onDelete={handleDelete}
            />
          </section>
        ))}
        {hasMore && (
          <div className="py-4 flex justify-center">
            <Button
              onClick={() => setPageSize(ps => ps + 30)}
              variant="outline"
              size="md"
              className="rounded-xl backdrop-blur-sm"
            >
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
// PHASE-2.03 END
